'use client'

import { useState, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import { useAuth } from '../contexts/AuthContext'
import { appOrigin, authenticatedFetch } from '@/lib/client-fetch'

const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || ''

// Validate that it's a publishable key (starts with pk_) and not a secret key (sk_)
const isValidPublishableKey = publishableKey.startsWith('pk_')
const isSecretKey = publishableKey.startsWith('sk_')

const stripePromise = publishableKey && isValidPublishableKey && !isSecretKey 
  ? loadStripe(publishableKey) 
  : null

interface PaymentButtonProps {
  orderId: string
  amount: number
  useCredits?: boolean
  onSuccess: () => void
  onError: (error: string) => void
}

function CheckoutForm({ orderId, amount, useCredits, onSuccess, onError }: PaymentButtonProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setLoading(true)

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${appOrigin()}/rider/orders?payment=success`,
      },
      redirect: 'if_required',
    })

    if (error) {
      onError(error.message || 'Payment failed')
      setLoading(false)
    } else if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture')) {
      // Payment confirmed. With manual capture the intent sits in
      // 'requires_capture' (card hold) until the order is delivered —
      // that's success from the customer's point of view.
      try {
        // Find the payment record by payment intent ID
        const paymentResponse = await authenticatedFetch(`/api/payments/by-intent/${paymentIntent.id}/complete`, {
          method: 'POST',
        })
        
        if (!paymentResponse.ok) {
          console.error('Failed to update payment status, but payment succeeded')
          // Don't block success if this fails - webhook will handle it
        }
      } catch (err) {
        console.error('Error updating payment status:', err)
        // Don't block success if this fails - webhook will handle it
      }
      
      onSuccess()
      setLoading(false)
    }
  }

  if (!stripe || !elements) {
    return (
      <div className="text-center py-4">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-ember"></div>
        <p className="text-gray-600 text-sm mt-2">Loading Stripe payment form...</p>
        <p className="text-gray-500 text-xs mt-1">This may take a few seconds</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement 
        options={{
          layout: 'tabs'
        }}
      />
      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full bg-ember text-white py-3 rounded-full font-semibold hover:bg-ember-hover shadow-md shadow-ember/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
      >
        {loading ? 'Processing...' : amount > 0 ? `Pay $${amount.toFixed(2)}` : 'Complete Order with Credits'}
      </button>
    </form>
  )
}

export default function PaymentButton({ orderId, amount, useCredits = false, onSuccess, onError }: PaymentButtonProps) {
  const { user } = useAuth()
  const [stripeInitError, setStripeInitError] = useState<string | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [isLoadingSecret, setIsLoadingSecret] = useState(true)
  const [initError, setInitError] = useState<string | null>(null)
  const [actualAmount, setActualAmount] = useState<number>(amount) // Track actual payment intent amount

  // Update actualAmount when amount prop changes (e.g., when order is created)
  useEffect(() => {
    setActualAmount(amount)
  }, [amount])

  useEffect(() => {
    // Validate key format first
    if (isSecretKey) {
      setStripeInitError('ERROR: You are using a SECRET KEY instead of a PUBLISHABLE KEY. Secret keys start with "sk_" and should never be used in the browser. Please update NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY in your .env.local file with a key that starts with "pk_".')
      return
    }

    if (!isValidPublishableKey && publishableKey) {
      setStripeInitError('Invalid Stripe publishable key format. It should start with "pk_test_" or "pk_live_".')
      return
    }

    // Check if Stripe promise resolves
    if (stripePromise) {
      stripePromise
        .then((stripe) => {
          if (!stripe) {
            setStripeInitError('Failed to load Stripe. Please check your publishable key.')
          }
        })
        .catch((error) => {
          console.error('Stripe initialization error:', error)
          if (error.message?.includes('secret key')) {
            setStripeInitError('ERROR: You are using a SECRET KEY instead of a PUBLISHABLE KEY. Please update NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY in your .env.local file with a key that starts with "pk_".')
          } else {
            setStripeInitError('Failed to initialize Stripe. Please refresh the page.')
          }
        })
    }
  }, [])

  // Fetch client secret before rendering Elements
  useEffect(() => {
    if (!user?.id) {
      setInitError('You must be logged in to make a payment')
      setIsLoadingSecret(false)
      return
    }

    // Removed user ID from console log for security
    console.log('Creating payment intent...', { orderId, amount })
    setIsLoadingSecret(true)
    setInitError(null)

    // Create payment intent with timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

    authenticatedFetch('/api/stripe/payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orderId,
        parentId: user.id,
        amount,
        useCredits,
      }),
      signal: controller.signal,
    })
      .then(async (res) => {
        clearTimeout(timeoutId)
        const data = await res.json()
        console.log('Payment intent response:', { status: res.status, data })
        
        if (!res.ok) {
          // If order is already paid, redirect to success instead of showing error
          if (data.error && (data.error.includes('already paid') || data.error.includes('already completed'))) {
            console.log('Order already paid, redirecting to success')
            onSuccess()
            return
          }
          throw new Error(data.error || `HTTP ${res.status}: Failed to create payment intent`)
        }
        
        // If fully paid with credits, call success immediately
        if (data.fullyPaidWithCredits) {
          console.log('Order fully paid with credits')
          onSuccess()
          return
        }
        
        if (data.clientSecret) {
          console.log('Payment intent created successfully')
          setClientSecret(data.clientSecret)
          // Update actual amount from payment intent response (this is the correct discounted amount)
          if (data.stripeAmount !== undefined) {
            setActualAmount(data.stripeAmount)
            console.log('Updated payment amount to:', data.stripeAmount)
          }
        } else {
          throw new Error(data.error || 'No client secret returned')
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        console.error('Payment intent error:', error)
        const errorMessage = error.name === 'AbortError' 
          ? 'Request timed out. Please try again.'
          : error.message || 'Failed to initialize payment'
        setInitError(errorMessage)
        onError(errorMessage)
      })
      .finally(() => {
        setIsLoadingSecret(false)
      })
  }, [orderId, amount, user?.id, onError])

  if (!publishableKey) {
    return (
      <div className="bg-red-50 border border-red-300 rounded-lg p-4">
        <p className="text-red-700 text-sm font-medium mb-2">Stripe Not Configured</p>
        <p className="text-red-600 text-xs">
          Please add <code className="bg-red-100 px-1 rounded">NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY</code> to your <code className="bg-red-100 px-1 rounded">.env.local</code> file.
        </p>
      </div>
    )
  }

  if (isSecretKey) {
    return (
      <div className="bg-red-50 border border-red-300 rounded-lg p-4">
        <p className="text-red-700 text-sm font-medium mb-2">⚠️ Security Error: Secret Key Detected</p>
        <p className="text-red-600 text-xs mb-2">
          You are using a <strong>SECRET KEY</strong> (starts with <code className="bg-red-100 px-1 rounded">sk_</code>) instead of a <strong>PUBLISHABLE KEY</strong> (starts with <code className="bg-red-100 px-1 rounded">pk_</code>).
        </p>
        <p className="text-red-600 text-xs mb-3">
          Secret keys should <strong>NEVER</strong> be used in the browser. They should only be used in server-side code.
        </p>
        <div className="bg-red-100 border border-red-200 rounded p-3 text-xs">
          <p className="text-red-800 font-medium mb-1">To fix this:</p>
          <ol className="list-decimal list-inside text-red-700 space-y-1">
            <li>Open your <code className="bg-red-200 px-1 rounded">.env.local</code> file</li>
            <li>Find <code className="bg-red-200 px-1 rounded">NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY</code></li>
            <li>Replace it with a key that starts with <code className="bg-red-200 px-1 rounded">pk_test_</code> or <code className="bg-red-200 px-1 rounded">pk_live_</code></li>
            <li>Restart your Next.js dev server</li>
          </ol>
        </div>
      </div>
    )
  }

  if (!isValidPublishableKey) {
    return (
      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
        <p className="text-yellow-700 text-sm font-medium mb-2">Invalid Stripe Key Format</p>
        <p className="text-yellow-600 text-xs">
          The publishable key should start with <code className="bg-yellow-100 px-1 rounded">pk_test_</code> or <code className="bg-yellow-100 px-1 rounded">pk_live_</code>.
        </p>
      </div>
    )
  }

  if (!stripePromise) {
    return (
      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
        <p className="text-yellow-700 text-sm">
          Failed to initialize Stripe. Please check your publishable key.
        </p>
      </div>
    )
  }

  if (stripeInitError) {
    return (
      <div className="bg-red-50 border border-red-300 rounded-lg p-4">
        <p className="text-red-700 text-sm font-medium mb-2">Stripe Initialization Error</p>
        <p className="text-red-600 text-xs mb-3">{stripeInitError}</p>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-red-700 hover:text-red-800 underline"
        >
          Refresh Page
        </button>
      </div>
    )
  }

  if (isLoadingSecret) {
    return (
      <div className="text-center py-4">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-ember"></div>
        <p className="text-gray-600 text-sm mt-2">Initializing payment...</p>
        <p className="text-gray-500 text-xs mt-1">This may take a few seconds</p>
      </div>
    )
  }

  if (initError) {
    return (
      <div className="bg-red-50 border border-red-300 rounded-lg p-4">
        <p className="text-red-700 text-sm font-medium mb-2">Failed to initialize payment</p>
        <p className="text-red-600 text-xs">{initError}</p>
        <button
          onClick={() => {
            setInitError(null)
            setIsLoadingSecret(true)
            setClientSecret(null)
            // Trigger re-fetch by updating orderId dependency
            window.location.reload()
          }}
          className="mt-3 text-sm text-red-700 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    )
  }

  if (!clientSecret) {
    return (
      <div className="text-center py-4">
        <p className="text-gray-600 text-sm">Waiting for payment setup...</p>
      </div>
    )
  }

  return (
    <Elements 
      stripe={stripePromise}
      options={{
        clientSecret: clientSecret, // Pass clientSecret here!
        appearance: {
          theme: 'stripe',
        },
        locale: 'en',
      }}
    >
      <CheckoutForm 
        orderId={orderId} 
        amount={actualAmount} 
        onSuccess={onSuccess} 
        onError={onError}
      />
    </Elements>
  )
}

